import React, { useState, useCallback } from 'react';
import useTTInsights from './features/tt-insights/useTTInsights';
import { fmtDate } from './features/tt-insights/utils';

// Tab components
import LeaderboardTab from './features/tt-insights/tabs/LeaderboardTab';
import WeeklyRebatesTab from './features/tt-insights/tabs/WeeklyRebatesTab';
import UserRebatesTab from './features/tt-insights/tabs/UserRebatesTab';
import SummaryTab from './features/tt-insights/tabs/SummaryTab';
import KpiTab from './features/tt-insights/tabs/KpiTab';
import NotificationsTab from './features/tt-insights/tabs/NotificationsTab';
import TopBuyersTab from './features/tt-insights/tabs/TopBuyersTab';
import BalancesAllTab from './features/tt-insights/tabs/BalancesAllTab';
import UserBalanceTab from './features/tt-insights/tabs/UserBalanceTab';
import NewModelMetricsTab from './features/tt-insights/tabs/NewModelMetricsTab';
import './features/tt-insights/styles/tt-insights.css';

// Styles
const styles = {
  container: {
    fontSize: '14px',
    backgroundColor: '#f8f9fa',
    minHeight: '100vh',
  },
  header: {
    paddingBottom: '1.5rem',
    borderBottom: '2px solid #e9ecef',
    marginBottom: '1.5rem',
  },
  headerTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#212529',
    margin: 0,
  },
  headerSubtitle: {
    fontSize: '13px',
    color: '#6c757d',
    margin: '0.5rem 0 0 0',
  },
  errorAlert: {
    borderRadius: '6px',
    borderLeft: '4px solid #dc3545',
    backgroundColor: '#fff5f5',
    padding: '1rem',
    marginBottom: '1.5rem',
  },
  errorIcon: {
    marginRight: '0.5rem',
    fontWeight: 'bold',
    color: '#dc3545',
  },
  controlsSection: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    padding: '1.25rem',
    marginBottom: '1.5rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    border: '1px solid #e9ecef',
  },
  controlsLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#495057',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '0.75rem',
    display: 'block',
  },
  tabsContainer: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    overflow: 'hidden',
  },
};

const TTInsights = () => {
  const {
    // state
    leaderboardCur,
    leaderboardPrev,
    monthCur,
    monthPrev,
    userCode,
    setUserCode,
    userRebates,
    pendingCount,
    rebateTotals,
    summary,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    notifications,
    notificationLimit,
    setNotificationLimit,
    topCurUsers,
    topPrevUsers,
    topCurPeriod,
    topPrevPeriod,
    topMeta,
    topLimit,
    setTopLimit,
    balances,
    balancesMeta,
    balancesLimit,
    setBalancesLimit,
    userBalance,
    newModelMetrics,
    error,
    loading,

    // derived
    curAll,
    prevAll,
    curTop20,
    prevTop20,
    rebatesCurNZ,
    rebatesPrev1NZ,
    rebatesPrev2NZ,
    kpiRows,

    // actions
    fetchUserRebates,
    fetchSummary,
    fetchKpiState,
    fetchUserNotifications,
    fetchTopBuyers,
    fetchBalances,
    fetchUserBalance,
    fetchLeaderboard,
    fetchWeeklyRebates,
    fetchNewModelMetrics,
  } = useTTInsights();

  const [activeTab, setActiveTab] = useState('leaderboard');

  // Memoized render functions for control sections
  const renderOverviewControls = useCallback(() => (
    <div className="row g-2 align-items-end">
      <div className="col-12 col-sm-6 col-md-3">
        <button
          className="btn btn-primary w-100"
          onClick={() => {
            fetchLeaderboard();
            fetchWeeklyRebates();
            fetchTopBuyers();
            fetchBalances();
          }}
          disabled={
            loading.board ||
            loading.weeks ||
            loading.topBuyers ||
            loading.balances
          }
        >
          {loading.board || loading.weeks || loading.topBuyers || loading.balances ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              Loading…
            </>
          ) : (
            '🔄 Refresh All'
          )}
        </button>
      </div>
    </div>
  ), [loading, fetchLeaderboard, fetchWeeklyRebates, fetchTopBuyers, fetchBalances]);

  const renderUserCodeInput = useCallback(
    (label = 'User Code', placeholder = 'Enter user code...') => (
      <div className="col-12 col-sm-6 col-md-4">
        <label className="form-label" style={styles.controlsLabel}>
          {label}
        </label>
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder={placeholder}
          value={userCode}
          onChange={(e) => setUserCode(e.target.value)}
        />
      </div>
    ),
    [userCode, setUserCode]
  );

  const renderDateRangeInputs = useCallback(() => (
    <>
      <div className="col-12 col-sm-6 col-md-3">
        <label className="form-label" style={styles.controlsLabel}>
          Date From
        </label>
        <input
          type="date"
          className="form-control form-control-sm"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
        />
      </div>
      <div className="col-12 col-sm-6 col-md-3">
        <label className="form-label" style={styles.controlsLabel}>
          Date To
        </label>
        <input
          type="date"
          className="form-control form-control-sm"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
        />
      </div>
    </>
  ), [dateFrom, setDateFrom, dateTo, setDateTo]);

  const renderDatePresets = useCallback(() => (
    <>
      <div className="col-12 col-sm-6 col-md-3">
        <button
          className="btn btn-outline-secondary w-100 btn-sm"
          onClick={() => {
            const dFrom = new Date();
            dFrom.setDate(dFrom.getDate() - 30);
            setDateFrom(fmtDate(dFrom));
            setDateTo(fmtDate(new Date()));
          }}
        >
          Last 30 Days
        </button>
      </div>
    </>
  ), [setDateFrom, setDateTo]);

  // All tabs definition
  const allTabs = [
    {
      key: 'leaderboard',
      label: '🏆 Leaderboard',
      content: (
        <LeaderboardTab
          loading={loading.board}
          monthCur={monthCur}
          monthPrev={monthPrev}
          curAll={curAll}
          prevAll={prevAll}
          curTop20={curTop20}
          prevTop20={prevTop20}
        />
      ),
    },
    {
      key: 'weekly',
      label: '📊 Weekly Rebates',
      content: (
        <WeeklyRebatesTab
          loading={loading.weeks}
          rebatesCurNZ={rebatesCurNZ}
          rebatesPrev1NZ={rebatesPrev1NZ}
          rebatesPrev2NZ={rebatesPrev2NZ}
        />
      ),
    },
    {
      key: 'top-buyers',
      label: '⭐ Top Buyers',
      content: (
        <TopBuyersTab
          loading={loading.topBuyers}
          fetchTopBuyers={fetchTopBuyers}
          topCurUsers={topCurUsers}
          topPrevUsers={topPrevUsers}
          topCurPeriod={topCurPeriod}
          topPrevPeriod={topPrevPeriod}
          topMeta={topMeta}
          topLimit={topLimit}
          setTopLimit={setTopLimit}
        />
      ),
    },
    {
      key: 'balances-all',
      label: '💳 Balances (All)',
      content: (
        <BalancesAllTab
          loading={loading.balances}
          fetchBalances={fetchBalances}
          balances={balances}
          balancesMeta={balancesMeta}
          balancesLimit={balancesLimit}
          setBalancesLimit={setBalancesLimit}
        />
      ),
    },
    {
      key: 'summary',
      label: '📋 Summary',
      content: summary ? (
        <SummaryTab loading={loading.summary} summary={summary} userCode={userCode} />
      ) : (
        <div className="text-muted text-center py-5">
          <p>📝 Enter a user code and click "Get Summary" to view user details</p>
        </div>
      ),
    },
    {
      key: 'notifications',
      label: '🔔 Notifications',
      badge: notifications.length || null,
      content: (
        <NotificationsTab
          loading={loading.notes}
          notifications={notifications}
          userCode={userCode}
        />
      ),
    },
    {
      key: 'user-rebates',
      label: '💰 User Rebates',
      badge: userRebates.length || null,
      content: (
        <UserRebatesTab
          loading={loading.rebates}
          userCode={userCode}
          pendingCount={pendingCount}
          rebateTotals={rebateTotals}
          userRebates={userRebates}
        />
      ),
    },
    {
      key: 'balance-user',
      label: '👤 My Balance',
      content: (
        <UserBalanceTab
          loading={loading.userBalance}
          fetchUserBalance={fetchUserBalance}
          userCode={userCode}
          setUserCode={setUserCode}
          userBalance={userBalance}
        />
      ),
    },
    {
      key: 'kpi',
      label: '📈 KPI State',
      badge: kpiRows.length || null,
      content: kpiRows.length > 0 ? (
        <KpiTab loading={loading.kpi} kpiRows={kpiRows} userCode={userCode} />
      ) : (
        <div className="text-muted text-center py-5">
          <p>📊 Enter a user code and click "Get KPI State" to view metrics</p>
        </div>
      ),
    },
    {
      key: 'new-model-metrics',
      label: '🆕 New Model Metrics',
      content: (
        <NewModelMetricsTab
          loading={loading.newModelMetrics}
          newModelMetrics={newModelMetrics}
          userCode={userCode}
          fetchNewModelMetrics={fetchNewModelMetrics}
        />
      ),
    },
  ];

  // Split tabs into two groups
  const allUsersTabs = allTabs.filter(t => 
    ['leaderboard', 'weekly', 'top-buyers', 'balances-all'].includes(t.key)
  );
  const individualUserTabs = allTabs.filter(t => 
    ['summary', 'notifications', 'user-rebates', 'balance-user', 'kpi', 'new-model-metrics'].includes(t.key)
  );

  const renderControls = () => {
    switch (activeTab) {
      // OVERVIEW TABS - Single refresh button
      case 'leaderboard':
      case 'weekly':
      case 'top-buyers':
      case 'balances-all':
        return (
          <div style={styles.controlsSection}>
            {renderOverviewControls()}
          </div>
        );

      // USER REBATES - Date range and user code
      case 'user-rebates':
        return (
          <div style={styles.controlsSection}>
            <div className="row g-2 align-items-end">
              {renderUserCodeInput('User Code')}
              {renderDateRangeInputs()}
              {renderDatePresets()}
              <div className="col-12 col-sm-6 col-md-3">
                <button
                  className="btn btn-success w-100 btn-sm"
                  onClick={fetchUserRebates}
                  disabled={!userCode.trim() || loading.rebates}
                >
                  {loading.rebates ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Loading…
                    </>
                  ) : (
                    '💰 Get Rebates'
                  )}
                </button>
              </div>
            </div>
          </div>
        );

      // SUMMARY - User code
      case 'summary':
        return (
          <div style={styles.controlsSection}>
            <div className="row g-2 align-items-end">
              {renderUserCodeInput('User Code')}
              <div className="col-12 col-sm-6 col-md-3">
                <button
                  className="btn btn-warning w-100 btn-sm"
                  onClick={fetchSummary}
                  disabled={!userCode.trim() || loading.summary}
                >
                  {loading.summary ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Loading…
                    </>
                  ) : (
                    '📋 Get Summary'
                  )}
                </button>
              </div>
            </div>
          </div>
        );

      // KPI - User code
      case 'kpi':
        return (
          <div style={styles.controlsSection}>
            <div className="row g-2 align-items-end">
              {renderUserCodeInput('User Code')}
              <div className="col-12 col-sm-6 col-md-3">
                <button
                  className="btn btn-info w-100 btn-sm"
                  onClick={fetchKpiState}
                  disabled={!userCode.trim() || loading.kpi}
                >
                  {loading.kpi ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Loading…
                    </>
                  ) : (
                    '📈 Get KPI State'
                  )}
                </button>
              </div>
            </div>
          </div>
        );

      // NOTIFICATIONS - User code
      case 'notifications':
        return (
          <div style={styles.controlsSection}>
            <div className="row g-2 align-items-end">
              {renderUserCodeInput('User Code')}
              <div className="col-6 col-sm-4 col-md-2">
                <label className="form-label form-label-sm mb-1">Notifications</label>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  min={1}
                  max={200}
                  value={notificationLimit}
                  onChange={(e) => setNotificationLimit(Math.max(1, parseInt(e.target.value) || 1))}
                />
              </div>
              <div className="col-12 col-sm-6 col-md-3">
                <button
                  className="btn btn-success w-100 btn-sm"
                  onClick={fetchUserNotifications}
                  disabled={!userCode.trim() || loading.notes}
                >
                  {loading.notes ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Loading…
                    </>
                  ) : (
                    '🔔 Get Notifications'
                  )}
                </button>
              </div>
            </div>
          </div>
        );

      // BALANCE (USER) - Controls are inside the tab already
      case 'balance-user':
        return null;

      // NEW MODEL METRICS - User code and fetch button
      case 'new-model-metrics':
        return (
          <div style={styles.controlsSection}>
            <div className="row g-2 align-items-end">
              {renderUserCodeInput('User Code')}
              <div className="col-12 col-sm-6 col-md-3">
                <button
                  className="btn btn-primary w-100 btn-sm"
                  onClick={fetchNewModelMetrics}
                  disabled={!userCode.trim() || loading.newModelMetrics}
                >
                  {loading.newModelMetrics ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Loading…
                    </>
                  ) : (
                    '🆕 Get New Model Metrics'
                  )}
                </button>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="container-fluid" style={styles.container}>
      <div className="px-3 py-4">
        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.headerTitle}>📊 TT Insights Dashboard</h1>
          <p style={styles.headerSubtitle}>
            Real-time analytics, rebates, and performance metrics
          </p>
        </div>

        {/* Error Alert */}
        {error && (
          <div style={styles.errorAlert}>
            <span style={styles.errorIcon}>⚠️</span>
            {error}
          </div>
        )}

        {/* Controls */}
        {renderControls()}

        {/* Tabs Container */}
        <div style={styles.tabsContainer}>
          <div className="tt-tabs">
            {/* All Users Tab Bar */}
            <div className="mb-2">
              <div className="text-muted mb-1" style={{ fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                All Users
              </div>
              <ul className="nav nav-tabs tt-tabbar mb-2" style={{ borderBottom: '2px solid #dee2e6' }}>
                {allUsersTabs.map((t) => (
                  <li className="nav-item" key={t.key}>
                    <button
                      type="button"
                      className={`nav-link ${activeTab === t.key ? "active" : ""}`}
                      onClick={() => setActiveTab(t.key)}
                    >
                      {t.label}
                      {t.badge != null && (
                        <span className="badge bg-secondary ms-2">{t.badge}</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Individual User Tab Bar */}
            <div className="mb-3">
              <div className="text-muted mb-1" style={{ fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Individual Users
              </div>
              <ul className="nav nav-tabs tt-tabbar mb-3" style={{ borderBottom: '2px solid #dee2e6' }}>
                {individualUserTabs.map((t) => (
                  <li className="nav-item" key={t.key}>
                    <button
                      type="button"
                      className={`nav-link ${activeTab === t.key ? "active" : ""}`}
                      onClick={() => setActiveTab(t.key)}
                    >
                      {t.label}
                      {t.badge != null && (
                        <span className="badge bg-secondary ms-2">{t.badge}</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Single Content Area */}
            <div className="tab-content">
              {allTabs.map((t) => (
                <div
                  key={t.key}
                  className={`tab-pane fade ${activeTab === t.key ? "show active" : ""}`}
                >
                  {t.content}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TTInsights;